<script context="module">
  export const selector = ".sustainability-calc__widget";
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";
  import type { Swiper as SwiperOld } from "swiper";
  import type {
    BusinessType,
    CalcProductSize,
    CalculationRequestParams,
    SustainabilityCalculatorPanelTypes,
    SustainabilityCalculatorService,
  } from "../../../typings/SustainabilityCalculator";
  import { Swiper, SwiperSlide } from "swiper/svelte";
  import {
    fetchCalculation,
    fetchSustainabilityCalculatorConstants,
    fetchSustainabilityCalculatorPanels,
    isBusinessType,
    isContainerRange,
    isConversion,
    isFrequencyRange,
    isOverview,
    isProductSelection,
    isServiceTypeSelection,
    isStory,
  } from "./SustainabilityCalculator";
  import RangeSlider from "svelte-range-slider-pips/src";
  import Select from "svelte-select/Select.svelte";
  import AtomicIconDropdown from "../Icons/SvelteSelectChevronIcon.svelte";
  import { isMobileViewport } from "../../utilities/ViewportHelpers";
  import type { SvelteSelectOption } from "../../../typings/SvelteSelect";
  import { NumberIncrementer } from "../../utilities/NumberIncrementer";
  import { formatNumberWithCommas } from "../../utilities/StringHelpers";
  import Tooltip from "bootstrap/js/src/tooltip";

  export let apiPathPanels: string = "";
  export let apiPathConstants: string = "";
  export let apiPathCalculation: string = "";
  export let sharethisLabel: string = "";

  let swiper: SwiperOld; // swiper object (non-svelte) for api methods to control slides

  // rest api data (presentation panels and services data)
  let presentationPanels: SustainabilityCalculatorPanelTypes[] = [];
  let servicesData: SustainabilityCalculatorService[] = [];

  // RENDERED PANELS: filtered down by service type selection and frequencyPart2
  $: filteredPanels = presentationPanels.filter((p) => {
    const isReadyToShowFrequencyPart2 =
      selectedService &&
      selectedService.Frequencies[frequencyRange[0]].FrequencyNestedMax > 0;

    if (p.FrequencyNestedMax === true) {
      return isReadyToShowFrequencyPart2;
    }

    return p.ServiceTypeId === "" || p.ServiceTypeId === serviceTypeRadios;
  });

  // determines if panel route is commercial
  // $: isCommercial = filteredPanels.some((p) => {
  //   return isBusinessType(p);
  // });

  // LOADING
  $: isLoading = filteredPanels.length < 1;

  // PANEL: service type selection
  let serviceTypeRadios: string = "";
  let showServiceTypeError: boolean = false;

  $: selectedService = servicesData?.find((s) => {
    return s.Id === serviceTypeRadios;
  });
  $: if (serviceTypeRadios.length) {
    setTimeout(() => {
      swiper.update();
    }, 300);
  }

  function serviceTypeSelectionContinue() {
    if (serviceTypeRadios.length) {
      showServiceTypeError = false;
      updateSliderAndSlideNext();
    } else {
      showServiceTypeError = true;
    }
  }

  // PANEL: product type selection
  function handleProductSelection(e, productTitle) {
    const selected = e.detail as CalcProductSize;

    productSizeSelectedValues = { [productTitle]: selected }; // changes the bind:value to the key of the current product dropdown so the rest of the product dropdowns are cleared
    productSizeSelected = selected;

    setTimeout(() => {
      swiper.updateAutoHeight();
      swiper.update();
    }, 300);
  }
  let productSizeSelected: CalcProductSize = null;
  let productSizeSelectedValues = {};

  // PANEL: container range selection
  let containerRangeValue: SvelteSelectOption = {
    index: 0,
    label: "1",
    value: "1",
  };

  let containerRangeValueInput: string = "";

  $: if (containerRangeValueInput && selectedService) {
    formatContainerSelectionInput(selectedService.QuantityMax);
  }

  function formatContainerSelectionInput(maxVal: number) {
    let tempNumber = parseInt(containerRangeValueInput);
    tempNumber = Math.floor(tempNumber);

    if (isNaN(tempNumber)) {
      containerRangeValueInput = "1";
      return;
    }
    if (tempNumber > maxVal) {
      containerRangeValueInput = tempNumber
        .toString()
        .slice(0, `${maxVal}`.length);
      return;
    }
  }

  // PANEL: frequency range selection
  let frequencyRange = [0];
  let frequencyRangeTimesPerWeek = [1]; // tracks commercial optional times per week range
  $: if (frequencyRangeTimesPerWeek[0] > 0 && swiper) {
    swiper.update();
  }

  function calculateConversionAndProceed() {
    const paramObj: CalculationRequestParams = {
      productSize: productSizeSelected.Index,
      containerCount: containerRangeValueInput.length
        ? containerRangeValueInput
        : containerRangeValue.value,
      frequencyRange: selectedFrequency?.Index,
    };
    if (selectedFrequency.FrequencyNestedMax) {
      paramObj.frequencyWeekly = frequencyRangeTimesPerWeek[0];
    }
    if (businessType) {
      paramObj.businessType = businessType.Index;
    }
    fetchCalculation(apiPathCalculation, paramObj).then((data) => {
      calculationUnit = data.CalculationUnit;
      calculationValue = data.CalculationValue;

      // add story panels
      presentationPanels = [...presentationPanels, ...data.StoryPanels];
      // presentationPanels.push(...data.StoryPanels);

      setTimeout(() => {
        swiper.update();
        setTimeout(() => {
          swiper.slideNext(700);

          // initialize tooltips
          const tooltipTriggerList = [].slice.call(
            swiper.el.querySelectorAll('[data-bs-toggle="tooltip"]')
          );
          tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
          });
        }, 300);
      }, 300);

      //conversionStat.style.setProperty("--percent", data.CalculationValue);
      new NumberIncrementer({
        el: conversionStat,
        speed: 10,
        decimal: 2,
        value: parseFloat(data.CalculationValue),
      });
    });
  }
  $: selectedFrequency = selectedService
    ? selectedService.Frequencies[frequencyRange[0]]
    : null;

  // PANEL: business type selection
  let businessType: BusinessType = undefined;
  let showBusinessTypeError = false;
  let businessTypeButtonNext: HTMLButtonElement;

  function handleBusinessTypeSelect(e) {
    businessType = e.detail;
    businessTypeButtonNext.focus();
    showBusinessTypeError = !businessType;
  }
  function handleBusinessTypeNextPanel() {
    showBusinessTypeError = !businessType;

    if (!showBusinessTypeError) {
      calculateConversionAndProceed();
    }

  }

  function dispatchCalculatorEngagementEvent(panel: SustainabilityCalculatorPanelTypes, selectedService?: SustainabilityCalculatorService, productSizeSelected?: CalcProductSize, quantity?: string, frequencyRangeLabel?: string, businessType?: string) {
    const calculatorEngagementEvent = new CustomEvent('calculatorEngagement', {
      detail: {
        panel,
        selectedService: selectedService?.Title || null,
        productSelection: productSizeSelected?.Title || null,
        quantity: quantity || null,
        frequency: frequencyRangeLabel || null,
        businessType: businessType || null,
      }
    });

    document.dispatchEvent(calculatorEngagementEvent);
  }

  // PANEL: conversion
  let conversionStat: HTMLElement;
  let calculationUnitReplace = "[CalculationUnit]";
  let calculationValueReplace = "[CalculationValue]";
  let calculationUnit: string = "";
  let calculationValue: string = "";

  function updateSliderAndSlideNext(shouldScrollTop = false) {
    swiper.update();
    swiper.slideNext(700);

    if (shouldScrollTop) {
      swiper.el.scrollIntoView();
    }
  }

  // PANEL: story
  let isEndSlide: boolean = false;
  $: if (isEndSlide && "__sharethis__" in window) {
    // once we reach the end slide, initialize the sharethis widget
    const st = __sharethis__ as any;
    st.init(st.config);
  }

  onMount(async () => {
    // fetch initial presentation panels
    fetchSustainabilityCalculatorPanels(apiPathPanels).then((panels) => {
      presentationPanels = panels;
    });

    // fetch service data
    servicesData = await fetchSustainabilityCalculatorConstants(
      apiPathConstants
    );
  });
</script>

{#if isLoading}
  <div class="sustainability-calc__loading">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      display="block"
      preserveAspectRatio="xMidYMid"
      style="margin:auto;background:0 0"
      viewBox="0 0 100 100"
    >
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="none"
        stroke="#c10016"
        stroke-dasharray="70.68583470577035 70.68583470577035"
        stroke-linecap="round"
        stroke-width="8"
      >
        <animateTransform
          attributeName="transform"
          dur="1.6666666666666667s"
          keyTimes="0;1"
          repeatCount="indefinite"
          type="rotate"
          values="0 50 50;360 50 50"
        />
      </circle>
    </svg>
  </div>
{:else}
  <!-- ShareThis BEGIN: FE mock only, does not work on localhost -->
  <div class="sharethis-wrapper" class:invisible={!isEndSlide}>
    {#if sharethisLabel.length}
      <p>{sharethisLabel}</p>
    {/if}

    <div class="sharethis-inline-share-buttons" />
    <!-- The HTML below comes from the ShareThis script. Only uncomment this for testing FE locally.  -->
    <!-- <div
      class="sharethis-inline-share-buttons st-left  st-inline-share-buttons st-animated"
      id="st-1"
    >
      <div
        class="st-btn st-first"
        data-network="facebook"
        style="display: inline-block;"
      >
        <img
          alt="facebook sharing button"
          src="https://platform-cdn.sharethis.com/img/facebook.svg"
        />
      </div>
      <div class="st-btn" data-network="twitter" style="display: inline-block;">
        <img
          alt="twitter sharing button"
          src="https://platform-cdn.sharethis.com/img/twitter.svg"
        />
      </div>
      <div
        class="st-btn"
        data-network="linkedin"
        style="display: inline-block;"
      >
        <img
          alt="linkedin sharing button"
          src="https://platform-cdn.sharethis.com/img/linkedin.svg"
        />
      </div>
      <div
        class="st-btn st-last"
        data-network="email"
        style="display: inline-block;"
      >
        <img
          alt="email sharing button"
          src="https://platform-cdn.sharethis.com/img/email.svg"
        />
      </div>
    </div> -->
  </div>
  <!-- ShareThis END -->

  <div
    class="sustainability-calc__slides"
    in:fade={{ delay: 100, duration: 500 }}
  >
    <Swiper
      simulateTouch={false}
      allowTouchMove={false}
      speed={700}
      autoHeight={isMobileViewport() ? true : false}
      effect={"fade"}
      fadeEffect={{ crossFade: true }}
      observeSlideChildren={true}
      watchSlidesProgress={true}
      on:slideChange={() => {
        setTimeout(() => {
          // if final slide
          if (
            swiper.slides.length === filteredPanels.length &&
            swiper.isEnd &&
            selectedFrequency !== null
          ) {
            isEndSlide = true;
          }

          // if conversion panel (replace with slot data 'isActive' once fixed: https://github.com/nolimits4web/swiper/issues/5349)
          if (isConversion(filteredPanels[swiper.activeIndex])) {
            if (process.env.dev) {
              console.log("conversion panel active");
            }
          }
        }, 600);
      }}
      on:swiper={(e) => {
        swiper = e.detail[0];
        // change other settings if needed
      }}
    >
      {#each filteredPanels as panel}
        {#if isOverview(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--start"}
          >
            <div class="sustainability-calc__panel-bg">
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcset={panel.BackgroundImage}
                />
                <img
                  src={panel.BackgroundImageMobile}
                  class="img-fluid"
                  alt="overview panel background"
                />
              </picture>
            </div>
            <section class="text-center sustainability-calc__panel-heading">
              <h1 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h1>
              <p>{@html panel.Description}</p>
              <button
                class="btn btn-primary sustainability-calc__panel-next"
                on:click={() => {
                  updateSliderAndSlideNext();
                  dispatchCalculatorEngagementEvent(panel);
                }}
                >{panel.ButtonLabel}</button
              >
            </section>
          </SwiperSlide>
        {:else if isServiceTypeSelection(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--service-type-selection"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {panel.Title}
              </h2>
              <p>{@html panel.Description}</p>
            </section>

            <div class="service-selection-asset">
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcset={serviceTypeRadios.length
                    ? servicesData.find((s) => s.Id === serviceTypeRadios)
                        .ImageSrc
                    : panel.NoSelectionImage}
                />
                <img
                  src={serviceTypeRadios.length
                    ? servicesData.find((s) => s.Id === serviceTypeRadios)
                        .ImageSrcMobile
                    : panel.NoSelectionImageMobile}
                  class="img-fluid"
                  alt="service selection panel background"
                />
              </picture>
            </div>

            <div class="service-radio-selection">
              {#each servicesData as service, i (service.Id)}
                <div class="form-check form-check--cartoon">
                  <label class="form-check-label" for={"serviceTypeRadios" + i}>
                    <input
                      class="form-check-input"
                      type="radio"
                      name="serviceTypeRadios"
                      id={"serviceTypeRadios" + i}
                      value={service.Id}
                      bind:group={serviceTypeRadios}
                      required
                    />
                    {service.Title}
                  </label>
                </div>
              {/each}
            </div>
            {#if showServiceTypeError && !serviceTypeRadios.length}
              <div class="sustainability-calc__panel-error-message mx-auto">
                {panel.RadioErrorMessage}
              </div>
            {/if}
            <button
              on:click={() => {
                serviceTypeSelectionContinue();
                dispatchCalculatorEngagementEvent(panel, selectedService);
              }}
              class="btn btn-primary sustainability-calc__panel-next"
              >{panel.ButtonLabel}
            </button>
          </SwiperSlide>
        {:else if isProductSelection(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--product-selection"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h2>
              <p>{@html panel.Description}</p>
            </section>
            <ul class="product-selection-cards list-unstyled">
              {#each selectedService.Products as product (product.Title)}
                <li>
                  <div
                    class="card card--icon"
                    class:card--active={product.ProductSizes.some(
                      (s) => s.Index === productSizeSelected?.Index
                    )}
                  >
                    <div class="card-image">
                      <img src={product.ImageSrc} alt="" />
                    </div>
                    <div class="card-body">
                      <div class="card-body-content">
                        <h3 class="card-title text-center">
                          {product.Title}
                        </h3>
                        <p class="card-description">{product.Description}</p>
                        <div
                          class="selection-wrapper selection-wrapper--chevron"
                        >
                          <Select
                            items={product.ProductSizes.map((ps) => ({ ...ps, label: ps.Title, value: ps.Index }))}
                            bind:value={productSizeSelectedValues[
                              product.Title
                            ]}
                            placeholder={product.ProductDropdownLabel}
                            listOffset={0}
                            id="sus-calc__bussiness-type-select"
                            showChevron={true}
                            ChevronIcon={AtomicIconDropdown}
                            on:select={(e) =>
                              handleProductSelection(e, product.Title)}
                            isClearable={false}
                            isSearchable={false}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              {/each}
            </ul>
            <div class="product-selection-bg-wrapper selection-bg-wrapper">
              <div class="sustainability-calc__panel-bg">
                <picture>
                  <source
                    media="(min-width: 768px)"
                    srcset={panel.ProductSelectionBackgroundImage}
                  />
                  <img
                    src={panel.ProductSelectionBackgroundImageMobile}
                    class="img-fluid"
                    alt="product selection panel background"
                  />
                </picture>
              </div>
              <button
                class="btn btn-primary sustainability-calc__panel-next"
                class:invisible={productSizeSelected == null}
                disabled={productSizeSelected == null}
                on:click={() => {
                  updateSliderAndSlideNext(true);
                  dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected);
                }}
                >{panel.ButtonLabel}
              </button>
            </div>
          </SwiperSlide>
        {:else if isContainerRange(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--container-range"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h2>
              <p>{@html panel.Description}</p>
            </section>

            {#if selectedService.QuantityMax > 10}
              <div
                class="container-selection container-input-selection mx-auto"
              >
                <input
                  type="text"
                  class="form-control"
                  bind:value={containerRangeValueInput}
                  placeholder="Enter a value 1-999"
                />
              </div>
            {:else}
              <div
                class="container-selection selection-wrapper selection-wrapper--chevron"
              >
                <Select
                  items={Array.from(
                    { length: selectedService.QuantityMax },
                    (_, i) => {
                      return {
                        index: i,
                        label: (i + 1).toString(),
                        value: (i + 1).toString(),
                      };
                    }
                  )}
                  listPlacement="bottom"
                  listOffset={0}
                  showChevron={true}
                  ChevronIcon={AtomicIconDropdown}
                  isClearable={false}
                  isSearchable={false}
                  bind:value={containerRangeValue}
                />
              </div>
            {/if}

            <div class="container-selection-bg-wrapper selection-bg-wrapper">
              <div class="sustainability-calc__panel-bg">
                <picture>
                  <source
                    media="(min-width: 768px)"
                    srcset={panel.ContainerRangeBackgroundImage}
                  />
                  <img
                    src={panel.ContainerRangeBackgroundImageMobile}
                    class="img-fluid"
                    alt="container range panel background"
                  />
                </picture>
              </div>
              <button
                class="btn btn-primary sustainability-calc__panel-next"
                class:invisible={containerRangeValueInput.length === 0 &&
                  selectedService.QuantityMax > 10}
                disabled={containerRangeValueInput.length === 0 &&
                  selectedService.QuantityMax > 10}
                on:click={() => {
                  updateSliderAndSlideNext();
                  dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value);
                }}
                >{panel.ButtonLabel}
              </button>
            </div>
          </SwiperSlide>
        {:else if isFrequencyRange(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--frequency-range"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h2>
              <p>{@html panel.Description}</p>
            </section>

            {#if selectedService && selectedService.Frequencies && panel.FrequencyNestedMax !== true}
              <div class="range-selection mx-auto">
                <span class="range-selection__label"
                  >{panel.RangeStartLabel}</span
                >
                <RangeSlider
                  pips
                  all="label"
                  bind:values={frequencyRange}
                  max={selectedService.Frequencies.length - 1}
                  formatter={(v) => selectedService.Frequencies[v].Label}
                />
                <span class="range-selection__label">{panel.RangeEndLabel}</span
                >
              </div>

              <!-- <p>
                {JSON.stringify(selectedService.Frequencies[frequencyRange[0]])}
              </p> -->

              <div class="frequency-selection-bg-wrapper selection-bg-wrapper">
                <div class="sustainability-calc__panel-bg">
                  <picture>
                    <source
                      media="(min-width: 768px)"
                      srcset={panel.FrequencyRangeBackgroundImage}
                    />
                    <img
                      src={panel.FrequencyRangeBackgroundImageMobile}
                      class="img-fluid"
                      alt="frequency range panel background"
                    />
                  </picture>
                </div>
                <button
                  class="btn btn-primary sustainability-calc__panel-next"
                  on:click={() => {
                    selectedFrequency.FrequencyNestedMax ||
                    selectedService.BusinessTypes.length
                      ? updateSliderAndSlideNext()
                      : calculateConversionAndProceed();
                      dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value, selectedService.Frequencies[frequencyRange[0]].Label);
                  }}
                  >{panel.ButtonLabel}
                </button>
              </div>
            {:else if selectedFrequency && selectedFrequency.FrequencyNestedMax}
              <!-- COMMERCIAL FREQUENCY PART 2: Before Business Type -->
              <div class="range-selection mx-auto">
                <span class="range-selection__label"
                  >{panel.RangeStartLabel}</span
                >
                <RangeSlider
                  pips
                  all="label"
                  bind:values={frequencyRangeTimesPerWeek}
                  min={1}
                  max={selectedFrequency.FrequencyNestedMax}
                />
                <span class="range-selection__label">{panel.RangeEndLabel}</span
                >
              </div>

              <div class="frequency-selection-bg-wrapper selection-bg-wrapper">
                <div class="sustainability-calc__panel-bg">
                  <picture>
                    <source
                      media="(min-width: 768px)"
                      srcset={panel.FrequencyRangeBackgroundImage}
                    />
                    <img
                      src={panel.FrequencyRangeBackgroundImageMobile}
                      class="img-fluid"
                      alt="frequency range part two panel background"
                    />
                  </picture>
                </div>
                <button
                  class="btn btn-primary sustainability-calc__panel-next"
                  on:click={() => {
                    updateSliderAndSlideNext();
                    dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value, selectedService.Frequencies[frequencyRange[0]].Label);
                  }}
                  >{panel.ButtonLabel}
                </button>
              </div>
            {/if}
          </SwiperSlide>
        {:else if isBusinessType(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--business-type"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h2>
              <p>{@html panel.Description}</p>
            </section>

            <div
              class="business-type-selection selection-wrapper selection-wrapper--chevron"
            >
              {#if selectedService !== null}
                <Select
                  items={selectedService.BusinessTypes.map((bt) => ({ ...bt, label: bt.Title, value: bt.Index }))}
                  placeholder={panel.BusinessTypeLabel}
                  listPlacement="bottom"
                  listOffset={0}
                  id="sus-calc__bussiness-type-select"
                  showChevron={true}
                  ChevronIcon={AtomicIconDropdown}
                  on:select={handleBusinessTypeSelect}
                  isClearable={false}
                  isSearchable={false}
                />
              {/if}
            </div>

            {#if showBusinessTypeError && !businessType}
              <div class="sustainability-calc__panel-error-message mx-auto">
                {panel.BusinessTypeErrorMessage}
              </div>
            {/if}

            <div class="business-type-bg-wrapper selection-bg-wrapper">
              <div class="sustainability-calc__panel-bg">
                <picture>
                  <source
                    media="(min-width: 768px)"
                    srcset={panel.BusinessTypeBackgroundImage}
                  />
                  <img
                    src={panel.BusinessTypeBackgroundImageMobile}
                    class="img-fluid"
                    alt="business type panel background"
                  />
                </picture>
              </div>
              <button
                class="btn btn-primary sustainability-calc__panel-next"
                on:click={() => {
                  handleBusinessTypeNextPanel();
                  dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value, selectedService.Frequencies[frequencyRange[0]].Label, businessType.Title);
                }}
                bind:this={businessTypeButtonNext}
                >{panel.ButtonLabel}
              </button>
            </div>
          </SwiperSlide>
        {:else if isConversion(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--conversion"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <h2 class="sustainability-calc__panel-heading-title">
                {@html panel.Title}
              </h2>
            </section>

            <section class="conversion-container container">
              <div class="conversion-container-stat-wrapper">
                <h3
                  class="conversion-stat"
                  bind:this={conversionStat}
                  data-inc-duration="2000"
                  data-inc-delay="200"
                >
                  0
                </h3>
                <p class="conversion-label">
                  {panel.NumberLabel.replace(
                    calculationUnitReplace,
                    calculationUnit
                  )}
                </p>
                <span class="conversion-label-sub">{panel.NumberSubLabel}</span>
              </div>
              <div class="conversion-container-asset-wrapper container">
                <img
                  src={panel.ConversionBackgroundImage}
                  class="img-fluid"
                  alt=""
                />
              </div>
            </section>

            <p class="conversion-description">{@html panel.Description}</p>
            <button
              class="btn btn-primary sustainability-calc__panel-next"
              on:click={() => {
                updateSliderAndSlideNext()
                if(selectedService?.Title === 'Residential') {
                  dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value, selectedService.Frequencies[frequencyRange[0]].Label)
                }
              }}
              >{panel.ButtonLabel}
            </button>
          </SwiperSlide>
        {:else if isStory(panel)}
          <SwiperSlide
            class={"sustainability-calc__panel sustainability-calc__panel--story"}
          >
            <section class="text-center sustainability-calc__panel-heading">
              <p class="sustainability-calc__panel-heading-subtitle">
                {panel.Description.replace(
                  calculationUnitReplace,
                  calculationUnit
                ).replace(calculationValueReplace, calculationValue)}
              </p>
              <h2 class="sustainability-calc__panel-heading-title">
                <span class="highlight highlight--primary"
                  >{formatNumberWithCommas(panel.Number)}</span
                >
                {@html panel.Title}
              </h2>
            </section>

            <section class="story-asset-container">
              <img src={panel.StoryImage} class="img-fluid" alt="" />
            </section>

            {#if panel.ButtonLink.length}
              {#if panel.BottomTitle.length}
                <div class="story-bottom-container">
                  <p>{panel.BottomTitle}</p>
                </div>
              {/if}
              <a
                class="btn btn-primary sustainability-calc__panel-next"
                href={panel.ButtonLink}
                on:click={() => dispatchCalculatorEngagementEvent(panel, selectedService, productSizeSelected, selectedService.QuantityMax > 10 ? containerRangeValueInput : containerRangeValue.value, selectedService.Frequencies[frequencyRange[0]].Label, businessType.Title)}
                >{panel.ButtonLabel}
              </a>
            {:else}
              <button
                class="btn btn-normal bi-arrow-right bi-arrow-right--after text-primary"
                on:click={() => updateSliderAndSlideNext()}
                >{panel.ButtonLabel}
              </button>
            {/if}

            {#if panel.CalculationDisclaimer.length}
              <div class="story-disclaimer">
                <div class="container">
                  <button
                    type="button"
                    class="btn btn-reset btn-normal"
                    data-bs-toggle="tooltip"
                    data-bs-html="true"
                    data-bs-custom-class="sustainability-calc"
                    title={panel.CalculationDisclaimer}
                  >
                    {@html panel.CalculationDisclaimerLabel}
                  </button>
                </div>
              </div>
            {/if}
          </SwiperSlide>
        {/if}
      {/each}
    </Swiper>
  </div>
{/if}

<style>
  /* @property --percent {
    syntax: "<number>";
    initial-value: 0;
    inherits: false;
  }
  @property --temp {
    syntax: "<number>";
    initial-value: 0;
    inherits: false;
  }
  @property --v1 {
    syntax: "<integer>";
    initial-value: 0;
    inherits: false;
  }
  @property --v2 {
    syntax: "<integer>";
    initial-value: 0;
    inherits: false;
  }

  .conversion-stat {
    transition: --percent 3s;
    --temp: calc(var(--percent));
    --v1: max(var(--temp) - 0.5, 0);
    --v2: max((var(--temp) - var(--v1)) * 100 - 0.5, 0);
    counter-reset: v1 var(--v1) v2 var(--v2);
  }
  .conversion-stat::before {
    content: counter(v1) "." counter(v2, decimal-leading-zero);
  } */
</style>
